import { useEffect, useId } from 'react';

// Registry of "there are unsaved edits on screen right now" signals.
//
// The re-auth coordinator (`@/auth/reauth`) MUST answer that question
// synchronously, from outside React (an axios 401 handler has no hooks), which
// is why this is a plain module-level set rather than Redux state. Each caller
// owns one opaque key; the set is non-empty exactly while at least one mounted
// form reports itself dirty.
//
// NF-07: nothing here is persisted — no localStorage, no sessionStorage, no
// form contents. It holds opaque keys only, in memory, for the lifetime of the
// page.
const dirtyKeys = new Set<string>();

export const registerUnsavedWork = (key: string): void => {
  dirtyKeys.add(key);
};

export const unregisterUnsavedWork = (key: string): void => {
  dirtyKeys.delete(key);
};

// Synchronous read for the coordinator.
export const hasUnsavedWork = (): boolean => dirtyKeys.size > 0;

// Test-only reset (module state outlives a single `render()`).
export const clearUnsavedWork = (): void => {
  dirtyKeys.clear();
};

// Dev-only probe, sibling of `__userManager` in `oidcConfig`. The registry is
// deliberately module-private (the coordinator is its only consumer), so this
// is the only way to confirm from a real browser that a dirty editor actually
// registered. Constant-folded out of the production bundle.
if (import.meta.env.DEV) {
  (window as unknown as { __unsavedWork?: unknown }).__unsavedWork = {
    has: (): boolean => hasUnsavedWork(),
    keys: (): string[] => [...dirtyKeys],
  };
}

// Hook form of the registry. `dirty` is the component's EXISTING dirty signal —
// never recompute one here. The key is a per-instance `useId()`, so two open
// editors register independently and an unmount always unregisters exactly its
// own entry.
export const useUnsavedWork = (dirty: boolean): void => {
  const key = useId();
  useEffect(() => {
    if (!dirty) {
      unregisterUnsavedWork(key);
      return;
    }
    registerUnsavedWork(key);
    return () => unregisterUnsavedWork(key);
  }, [key, dirty]);
};
